import { useState, useEffect, useRef } from "react";
import { Shield, FileText, Image, Mic, Video, Link2, ShieldAlert, CheckCircle, Star, ChevronRight, Users, DollarSign, Zap, Award, MessageSquare, HelpCircle, ArrowRight, Play, Globe, Lock, TrendingUp, Gift, Wallet, Phone, Mail, MapPin, Bug, ChevronLeft, ExternalLink, ShoppingBag, Tag } from "lucide-react";

const LandingPage = ({ onGetStarted }) => {
  const [detectedCount, setDetectedCount] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeFaq, setActiveFaq] = useState(null);
  const [carouselProducts, setCarouselProducts] = useState([]);
  const carouselRef = useRef(null);

  // Fetch marketplace products
  useEffect(() => {
    fetch("https://garage-sell.preview.emergentagent.com/api/products/carousel?limit=12")
      .then(res => res.json())
      .then(data => setCarouselProducts(data.products || []))
      .catch(() => {});
  }, []);

  const getProductImage = (images) => {
    if (!images || images.length === 0) return null;
    const img = images[0];
    if (img.startsWith("http")) return img;
    return `https://garage-sell.preview.emergentagent.com/api/files/${img}`;
  };

  const scrollCarousel = (direction) => {
    if (!carouselRef.current) return;
    const scrollAmount = 320;
    carouselRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
  };

  // Animated counter
  useEffect(() => {
    const target = 15847;
    const duration = 2000;
    const step = target / (duration / 16);
    let current = 0;
    
    const timer = setInterval(() => {
      current += step;
      if (current >= target) {
        setDetectedCount(target);
        clearInterval(timer);
      } else {
        setDetectedCount(Math.floor(current));
      }
    }, 16);

    return () => clearInterval(timer);
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 7);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    { icon: FileText, title: "Verificar Textos", desc: "Detecte se um texto é falso ou suspeito", color: "cyan", cost: 2 },
    { icon: Image, title: "Verificar Imagens", desc: "Identifique imagens manipuladas ou falsas", color: "purple", cost: 4 },
    { icon: Mic, title: "Verificar Áudios", desc: "Detecte vozes clonadas e sintéticas", color: "emerald", cost: 6 },
    { icon: Video, title: "Verificar Vídeos", desc: "Identifique deepfakes e vídeos falsos", color: "rose", cost: 20 },
    { icon: Link2, title: "Verificar Links", desc: "Detecte links maliciosos e phishing", color: "blue", cost: 2 },
    { icon: Bug, title: "Antivírus", desc: "Escaneie arquivos com 70+ motores antivírus", color: "amber", cost: 3 },
    { icon: ShieldAlert, title: "Golpe Alert", desc: "Análise especializada em golpes brasileiros", color: "orange", cost: 0 },
  ];

  const packages = [
    { name: "Starter", credits: 25, price: "9,90", desc: "Para uso ocasional", popular: false },
    { name: "Plus", credits: 75, price: "24,90", desc: "Para uso regular", popular: false },
    { name: "Pro", credits: 250, price: "69,90", desc: "Para uso intensivo", popular: true },
    { name: "Enterprise", credits: 1000, price: "199,00", desc: "Para empresas", popular: false },
  ];

  const testimonials = [
    // Depoimentos serão adicionados pelo usuário
  ];

  const faqs = [
    {
      q: "O que é o WISE EAGLE?",
      a: "WISE EAGLE é uma plataforma de tecnologia avançada para detectar conteúdos falsos, golpes digitais, deepfakes e links maliciosos. Protegemos você contra fraudes online."
    },
    {
      q: "Como funciona o sistema de créditos?",
      a: "Cada verificação consome créditos. Textos e links custam 2 créditos, imagens 4 créditos, áudios 6 créditos e vídeos 20 créditos. Novos usuários ganham 10 créditos grátis!"
    },
    {
      q: "O Golpe Alert é pago?",
      a: "Não! O Golpe Alert é uma funcionalidade gratuita que pode ser ativada em qualquer verificação. Ele analisa padrões específicos de golpes brasileiros."
    },
    {
      q: "Como funciona o programa de indicação?",
      a: "Você e seu indicado ganham 5 créditos grátis! Além disso, quando seus indicados compram qualquer plano de créditos, você ganha recompensas em cripto (USDT/USDC): 10% nível 1, 3% nível 2, 1% nível 3."
    },
    {
      q: "Como recebo minhas recompensas?",
      a: "As recompensas são acumuladas em sua conta WISE EAGLE. Quando atingir o valor mínimo de $100.00 em USDT/USDC, você pode retirar manualmente para sua conta na exchange OASIS e de lá transferir para qualquer carteira cripto."
    },
    {
      q: "Meus dados estão seguros?",
      a: "Sim! Seus dados são criptografados e nunca compartilhamos informações pessoais. As verificações são processadas de forma segura e privada."
    },
  ];

  const steps = [
    { num: "1", title: "Cole o Conteúdo", desc: "Texto, imagem, áudio, vídeo ou link suspeito" },
    { num: "2", title: "Análise Rápida", desc: "Nosso sistema verifica em segundos" },
    { num: "3", title: "Resultado Claro", desc: "Saiba se é seguro ou perigoso" },
  ];

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header/Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-10 h-10 rounded-full" />
            <div>
              <h1 className="text-lg font-bold text-white">WISE EAGLE</h1>
              <p className="text-xs text-zinc-500 hidden sm:block">Proteja-se contra conteúdo falso</p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm text-zinc-400 hover:text-white transition-colors">Funcionalidades</a>
            <a href="#pricing" className="text-sm text-zinc-400 hover:text-white transition-colors">Preços</a>
            <a href="#referral" className="text-sm text-zinc-400 hover:text-white transition-colors">Indicação</a>
            <a href="#faq" className="text-sm text-zinc-400 hover:text-white transition-colors">FAQ</a>
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={onGetStarted}
              className="px-5 py-2 rounded-full border border-zinc-600 hover:border-zinc-400 text-zinc-300 hover:text-white font-semibold text-sm transition-all"
            >
              Login
            </button>
            <button
              onClick={onGetStarted}
              className="px-5 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold text-sm transition-all shadow-lg shadow-cyan-500/25"
            >
              Começar Grátis
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 via-transparent to-transparent" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
        <div className="absolute top-40 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-12 items-center">
            {/* Left Column - Headline */}
            <div className="lg:col-span-3 text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 mb-6">
                <Shield className="w-4 h-4 text-cyan-400" />
                <span className="text-sm text-cyan-300">Proteção avançada contra golpes digitais</span>
              </div>

              {/* Main headline */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5">
                <span className="text-white">Detecte </span>
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Conteúdo Falso
                </span>
                <br />
                <span className="text-white">em Segundos</span>
              </h1>

              <p className="text-base md:text-lg text-zinc-400 mb-8 max-w-xl mx-auto lg:mx-0">
                Proteja você e sua família contra golpes, deepfakes, phishing, malwares e conteúdos falsos. 
                Antivírus com 70+ motores e verificação de IA rápida e precisa.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-8">
                <button
                  onClick={onGetStarted}
                  className="w-full sm:w-auto px-8 py-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold text-lg transition-all shadow-xl shadow-cyan-500/30 flex items-center justify-center gap-2"
                >
                  Começar Grátis
                  <ArrowRight className="w-5 h-5" />
                </button>
                <a
                  href="#demo"
                  className="w-full sm:w-auto px-8 py-4 rounded-xl bg-zinc-800/50 hover:bg-zinc-800 border border-zinc-700 text-white font-semibold text-lg transition-all flex items-center justify-center gap-2"
                >
                  <Play className="w-5 h-5" />
                  Ver Demonstração
                </a>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0">
                <div className="text-center lg:text-left">
                  <p className="text-2xl md:text-3xl font-bold text-cyan-400">{detectedCount.toLocaleString()}+</p>
                  <p className="text-xs text-zinc-500">Golpes Detectados</p>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-2xl md:text-3xl font-bold text-emerald-400">99.2%</p>
                  <p className="text-xs text-zinc-500">Precisão</p>
                </div>
                <div className="text-center lg:text-left">
                  <p className="text-2xl md:text-3xl font-bold text-purple-400">5k+</p>
                  <p className="text-xs text-zinc-500">Usuários Ativos</p>
                </div>
              </div>
            </div>

            {/* Right Column - Partners */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              {/* OASIS Partner Card */}
              <a
                href="https://crypto-hub-rebuild.emergent.host"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="oasis-partner-card"
                className="block rounded-2xl bg-gradient-to-br from-zinc-800/90 to-zinc-900/90 border border-zinc-700/60 hover:border-amber-500/40 p-5 transition-all duration-300 group hover:shadow-lg hover:shadow-amber-500/5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center overflow-hidden">
                    <img src="/oasis_logo.png" alt="OASIS" className="w-8 h-8 rounded" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">OASIS Exchange</h3>
                    <p className="text-zinc-500 text-xs">Exchange parceira</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-amber-400 ml-auto transition-colors" />
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Compre e venda <span className="text-amber-400 font-medium">criptomoedas</span> e faça transações via <span className="text-amber-400 font-medium">PIX</span> na nossa exchange parceira.
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-md bg-amber-500/10 text-amber-300 text-xs font-medium border border-amber-500/20">Cripto</span>
                  <span className="px-2.5 py-1 rounded-md bg-amber-500/10 text-amber-300 text-xs font-medium border border-amber-500/20">PIX</span>
                  <span className="px-2.5 py-1 rounded-md bg-amber-500/10 text-amber-300 text-xs font-medium border border-amber-500/20">Seguro</span>
                </div>
              </a>

              {/* Venda de Garagem Partner Card */}
              <a
                href="https://garage-sell.preview.emergentagent.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="marketplace-partner-card"
                className="block rounded-2xl bg-gradient-to-br from-zinc-800/90 to-zinc-900/90 border border-zinc-700/60 hover:border-emerald-500/40 p-5 transition-all duration-300 group hover:shadow-lg hover:shadow-emerald-500/5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Venda de Garagem</h3>
                    <p className="text-zinc-500 text-xs">Marketplace parceiro</p>
                  </div>
                  <ExternalLink className="w-4 h-4 text-zinc-600 group-hover:text-emerald-400 ml-auto transition-colors" />
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed">
                  Compre e venda <span className="text-emerald-400 font-medium">produtos</span>, <span className="text-emerald-400 font-medium">serviços</span> e <span className="text-emerald-400 font-medium">digitais</span> no nosso marketplace parceiro.
                </p>
                {/* Mini product preview */}
                {carouselProducts.length > 0 && (
                  <div className="mt-3 flex gap-2 overflow-hidden">
                    {carouselProducts.slice(0, 4).map((p) => (
                      <div key={p._id} className="w-14 h-14 rounded-lg bg-zinc-900 overflow-hidden flex-shrink-0 border border-zinc-700/50">
                        {getProductImage(p.images) ? (
                          <img src={getProductImage(p.images)} alt="" className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><ShoppingBag className="w-4 h-4 text-zinc-700" /></div>
                        )}
                      </div>
                    ))}
                    <div className="w-14 h-14 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex-shrink-0 flex items-center justify-center">
                      <span className="text-emerald-400 text-xs font-bold">+{Math.max(0, carouselProducts.length - 4)}</span>
                    </div>
                  </div>
                )}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section id="demo" className="py-16 md:py-24 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Veja o WISE EAGLE em Ação</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Descubra como nossa plataforma protege você contra golpes e ameaças digitais</p>
          </div>

          <div className="relative rounded-2xl overflow-hidden border border-zinc-700 shadow-2xl shadow-cyan-500/10 bg-zinc-800" data-testid="video-section">
            <div className="aspect-video">
              <iframe
                src="https://www.youtube.com/embed/ubatkVmfdW8?rel=0"
                title="WISE EAGLE - Apresentação"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* How it Works - Visual */}
      <section className="py-16 md:py-24 bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Como Funciona</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Verificação em 3 passos simples</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 - Cole o Conteúdo */}
            <div className="relative">
              <div className="bg-zinc-800/50 rounded-2xl p-6 border border-zinc-700 hover:border-cyan-500/50 transition-all group h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-xl font-bold text-white mb-5 group-hover:scale-110 transition-transform">
                  1
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Cole o Conteúdo</h3>
                <p className="text-zinc-400 text-sm mb-5">Texto, imagem, áudio, vídeo ou link suspeito</p>
                
                {/* Visual Demo - Content Types */}
                <div className="bg-zinc-900/80 rounded-xl p-4 border border-zinc-700">
                  <div className="grid grid-cols-5 gap-2">
                    <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                      <FileText className="w-5 h-5 text-cyan-400" />
                      <span className="text-xs text-zinc-400">Texto</span>
                    </div>
                    <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <Image className="w-5 h-5 text-purple-400" />
                      <span className="text-xs text-zinc-400">Imagem</span>
                    </div>
                    <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                      <Mic className="w-5 h-5 text-emerald-400" />
                      <span className="text-xs text-zinc-400">Áudio</span>
                    </div>
                    <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-rose-500/10 border border-rose-500/30">
                      <Video className="w-5 h-5 text-rose-400" />
                      <span className="text-xs text-zinc-400">Vídeo</span>
                    </div>
                    <div className="flex flex-col items-center gap-1 p-2 rounded-lg bg-blue-500/10 border border-blue-500/30">
                      <Link2 className="w-5 h-5 text-blue-400" />
                      <span className="text-xs text-zinc-400">Link</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <ChevronRight className="w-8 h-8 text-zinc-600" />
              </div>
            </div>

            {/* Step 2 - Análise Rápida */}
            <div className="relative">
              <div className="bg-zinc-800/50 rounded-2xl p-6 border border-zinc-700 hover:border-cyan-500/50 transition-all group h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-xl font-bold text-white mb-5 group-hover:scale-110 transition-transform">
                  2
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Análise Rápida</h3>
                <p className="text-zinc-400 text-sm mb-5">Nosso sistema verifica em segundos</p>
                
                {/* Visual Demo - Scanning Animation */}
                <div className="bg-zinc-900/80 rounded-xl p-4 border border-zinc-700">
                  <div className="flex flex-col items-center gap-3">
                    <div className="relative w-16 h-16">
                      <div className="absolute inset-0 rounded-full border-4 border-cyan-500/20"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-cyan-500 animate-spin"></div>
                      <div className="absolute inset-2 rounded-full bg-zinc-800 flex items-center justify-center">
                        <Shield className="w-6 h-6 text-cyan-400" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></div>
                      <span className="text-sm text-cyan-400">Analisando...</span>
                    </div>
                    <div className="w-full bg-zinc-700 rounded-full h-2 overflow-hidden">
                      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full rounded-full animate-pulse" style={{ width: '70%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                <ChevronRight className="w-8 h-8 text-zinc-600" />
              </div>
            </div>

            {/* Step 3 - Resultado Claro */}
            <div className="relative">
              <div className="bg-zinc-800/50 rounded-2xl p-6 border border-zinc-700 hover:border-cyan-500/50 transition-all group h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-xl font-bold text-white mb-5 group-hover:scale-110 transition-transform">
                  3
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Resultado Claro</h3>
                <p className="text-zinc-400 text-sm mb-5">Saiba se é seguro ou perigoso</p>
                
                {/* Visual Demo - Results */}
                <div className="bg-zinc-900/80 rounded-xl p-4 border border-zinc-700 space-y-3">
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <div className="flex-1">
                      <span className="text-sm text-emerald-400 font-semibold">Seguro</span>
                      <div className="w-full bg-zinc-700 rounded-full h-1.5 mt-1">
                        <div className="bg-emerald-500 h-full rounded-full" style={{ width: '95%' }}></div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-2 rounded-lg bg-red-500/10 border border-red-500/30">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                    <div className="flex-1">
                      <span className="text-sm text-red-400 font-semibold">Golpe Detectado!</span>
                      <div className="w-full bg-zinc-700 rounded-full h-1.5 mt-1">
                        <div className="bg-red-500 h-full rounded-full" style={{ width: '87%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Funcionalidades</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Proteção completa contra conteúdo falso e ameaças digitais</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              const isActive = activeFeature === i;
              return (
                <div
                  key={i}
                  className={`p-6 rounded-2xl border transition-all cursor-pointer ${
                    isActive 
                      ? `bg-${feature.color}-500/10 border-${feature.color}-500/50` 
                      : 'bg-zinc-800/30 border-zinc-700 hover:border-zinc-600'
                  }`}
                  onMouseEnter={() => setActiveFeature(i)}
                >
                  <div className={`w-12 h-12 rounded-xl bg-${feature.color}-500/20 flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 text-${feature.color}-400`} />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-zinc-400 text-sm mb-3">{feature.desc}</p>
                  {feature.cost > 0 && (
                    <span className="text-xs text-zinc-500">{feature.cost} créditos</span>
                  )}
                  {feature.cost === 0 && (
                    <span className="text-xs text-emerald-400 font-semibold">Grátis</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Antivirus Section */}
      <section className="py-16 md:py-24 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-medium mb-4">
                <Bug className="w-3 h-3" />
                NOVO
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Antivírus com <span className="text-amber-400">70+ Motores</span>
              </h2>
              <p className="text-zinc-400 mb-6">
                Escaneie qualquer arquivo suspeito contra os maiores antivírus do mundo em uma única análise. 
                Avast, Kaspersky, BitDefender, Norton, Malwarebytes, ESET e dezenas de outros verificam seu arquivo simultaneamente.
              </p>
              <div className="p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 mb-6">
                <p className="text-sm text-zinc-300 leading-relaxed">
                  <strong className="text-amber-400">Explicando de forma simples:</strong> Recebeu um arquivo pelo WhatsApp, 
                  e-mail ou baixou da internet e não sabe se é seguro? Pode ser uma foto, documento ou programa. 
                  Basta clicar no botão abaixo, escolher o arquivo do seu celular ou computador (igual quando envia uma foto no WhatsApp) 
                  e mais de 70 antivírus famosos analisam ao mesmo tempo — resultado em segundos!
                </p>
              </div>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-zinc-300">Resultado em segundos contra 70+ antivírus</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-zinc-300">Detecta vírus, trojans, ransomware e malwares</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-zinc-300">Relatório detalhado com nível de risco</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                  <span className="text-zinc-300">Arquivos até 32MB — apenas 3 créditos</span>
                </div>
              </div>
              <button
                onClick={onGetStarted}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-bold transition-all shadow-lg shadow-amber-500/20 flex items-center gap-2"
              >
                <Shield className="w-5 h-5" />
                Escanear Arquivo Agora
              </button>
            </div>

            <div className="relative">
              <div className="p-6 rounded-2xl bg-zinc-800/50 border border-zinc-700">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <ShieldAlert className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold">malware_exemplo.exe</p>
                    <p className="text-xs text-red-400">Ameaça detectada!</p>
                  </div>
                </div>
                <div className="flex gap-1 h-3 rounded-full overflow-hidden bg-zinc-900 mb-3">
                  <div className="bg-red-500 rounded-full" style={{width: '80%'}} />
                  <div className="bg-zinc-600 rounded-full" style={{width: '12%'}} />
                  <div className="bg-emerald-500 rounded-full" style={{width: '8%'}} />
                </div>
                <div className="grid grid-cols-3 gap-2 text-center text-xs mb-4">
                  <div><span className="text-red-400 font-bold">61</span><br/><span className="text-zinc-500">Malicioso</span></div>
                  <div><span className="text-zinc-400 font-bold">6</span><br/><span className="text-zinc-500">N/Detectado</span></div>
                  <div><span className="text-emerald-400 font-bold">9</span><br/><span className="text-zinc-500">Seguro</span></div>
                </div>
                <div className="space-y-2">
                  {['Avast', 'Kaspersky', 'BitDefender', 'Malwarebytes', 'Norton'].map((name) => (
                    <div key={name} className="flex justify-between items-center p-2 rounded-lg bg-zinc-900/50 text-xs">
                      <span className="text-zinc-300">{name}</span>
                      <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">Detectado</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 md:py-24 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Planos e Preços</h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Escolha o pacote ideal para você. Comece com 10 créditos grátis!</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {packages.map((pkg, i) => (
              <div
                key={i}
                className={`relative p-6 rounded-2xl border ${
                  pkg.popular 
                    ? 'bg-gradient-to-b from-cyan-500/10 to-blue-500/10 border-cyan-500/50' 
                    : 'bg-zinc-800/30 border-zinc-700'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-xs font-bold text-white">
                    POPULAR
                  </div>
                )}
                <h3 className="text-xl font-bold text-white mb-1">{pkg.name}</h3>
                <p className="text-sm text-zinc-500 mb-4">{pkg.desc}</p>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-white">R$ {pkg.price}</span>
                </div>
                <p className="text-cyan-400 font-semibold mb-6">{pkg.credits} créditos</p>
                <button
                  onClick={onGetStarted}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    pkg.popular
                      ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white'
                      : 'bg-zinc-700 hover:bg-zinc-600 text-white'
                  }`}
                >
                  Comprar
                </button>
              </div>
            ))}
          </div>

          {/* Credits info */}
          <div className="mt-12 max-w-3xl mx-auto">
            <div className="bg-zinc-800/50 rounded-2xl p-6 border border-zinc-700">
              <h4 className="font-semibold text-white mb-4 text-center">Custo por verificação</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div>
                  <FileText className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
                  <p className="text-sm text-white">Texto</p>
                  <p className="text-xs text-zinc-500">2 créditos</p>
                </div>
                <div>
                  <Image className="w-5 h-5 text-purple-400 mx-auto mb-1" />
                  <p className="text-sm text-white">Imagem</p>
                  <p className="text-xs text-zinc-500">4 créditos</p>
                </div>
                <div>
                  <Mic className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                  <p className="text-sm text-white">Áudio</p>
                  <p className="text-xs text-zinc-500">6 créditos</p>
                </div>
                <div>
                  <Video className="w-5 h-5 text-rose-400 mx-auto mb-1" />
                  <p className="text-sm text-white">Vídeo</p>
                  <p className="text-xs text-zinc-500">20 créditos</p>
                </div>
                <div>
                  <Link2 className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-sm text-white">Link</p>
                  <p className="text-xs text-zinc-500">2 créditos</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Referral Program */}
      <section id="referral" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-gradient-to-br from-emerald-500/10 via-cyan-500/10 to-blue-500/10 rounded-3xl p-8 md:p-12 border border-emerald-500/30 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl" />
            
            <div className="relative">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Gift className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">Programa de Indicação</h2>
                  <p className="text-emerald-400">Ganhe em cripto indicando amigos!</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-cyan-400" />
                    Bônus de Indicação
                  </h3>
                  <p className="text-zinc-400 mb-4">
                    Você e seu indicado ganham <span className="text-cyan-400 font-bold">5 créditos grátis</span> cada 
                    quando alguém se cadastra usando seu link!
                  </p>

                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2 mt-6">
                    <Wallet className="w-5 h-5 text-emerald-400" />
                    Programa de Indicação (Member get Member)
                  </h3>
                  <p className="text-zinc-400 mb-2">
                    Ganhe cripto (<span className="text-emerald-400 font-bold">USDT</span>) quando seus indicados 
                    comprarem qualquer plano de créditos:
                  </p>
                </div>

                <div>
                  <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-700">
                    <h4 className="font-semibold text-white mb-4">Sistema Multinível (3 níveis)</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                        <span className="text-sm text-zinc-300">Nível 1 - Indicação direta</span>
                        <span className="font-bold text-cyan-400">10%</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                        <span className="text-sm text-zinc-300">Nível 2 - Indicado do indicado</span>
                        <span className="font-bold text-emerald-400">3%</span>
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                        <span className="text-sm text-zinc-300">Nível 3 - Terceiro nível</span>
                        <span className="font-bold text-amber-400">1%</span>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500 mt-4">
                      Saque mínimo: $100.00 | Recompensas via OASIS Exchange
                    </p>
                  </div>
                </div>
              </div>

              <button
                onClick={onGetStarted}
                className="mt-8 px-8 py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white font-bold transition-all shadow-lg shadow-emerald-500/25"
              >
                Começar a Indicar
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* OASIS Partner Banner - Seção Grande */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10" />
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="max-w-6xl mx-auto px-4 relative">
          <div className="bg-gradient-to-br from-zinc-900/90 via-zinc-800/90 to-zinc-900/90 rounded-3xl p-8 md:p-12 border border-cyan-500/30 shadow-2xl shadow-cyan-500/10">
            <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
              {/* Logo OASIS */}
              <div className="flex-shrink-0">
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-white/10 backdrop-blur-sm p-4 border border-cyan-500/30 shadow-lg shadow-cyan-500/20">
                  <img 
                    src="/oasis_logo.png" 
                    alt="OASIS Exchange" 
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
              
              {/* Content */}
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20 border border-cyan-500/40 mb-4">
                  <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                  <span className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">Parceiro Oficial</span>
                </div>
                
                <h2 className="text-2xl md:text-4xl font-bold text-white mb-3">
                  Conheça a <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">OASIS</span>
                </h2>
                
                <p className="text-zinc-400 mb-6 max-w-xl">
                  Nossa exchange parceira para recompensas em <span className="text-cyan-400 font-semibold">USDT/USDC</span>. 
                  Compre, venda e troque criptomoedas com segurança e as melhores taxas do mercado.
                </p>
                
                <div className="flex flex-col sm:flex-row items-center gap-4 justify-center md:justify-start">
                  <a
                    href="https://crypto-hub-rebuild.emergent.host"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full sm:w-auto px-8 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold text-lg transition-all shadow-lg shadow-cyan-500/30 flex items-center justify-center gap-2 group"
                  >
                    Acessar OASIS
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                  <div className="flex items-center gap-4 text-sm text-zinc-500">
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      Seguro
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      Rápido
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      Confiável
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - só mostra se houver depoimentos */}
      {testimonials.length > 0 && (
        <section className="py-16 md:py-24 bg-zinc-900/50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">O que dizem nossos usuários</h2>
              <p className="text-zinc-400">Histórias reais de pessoas protegidas pelo WISE EAGLE</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-zinc-800/50 rounded-2xl p-6 border border-zinc-700">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(t.rating)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <p className="text-zinc-300 mb-6 italic">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                      {t.avatar}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{t.name}</p>
                      <p className="text-sm text-zinc-500">{t.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Venda de Garagem - Marketplace Carousel */}
      {carouselProducts.length > 0 && (
        <section className="py-16 md:py-24 bg-zinc-900/40 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-10">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-emerald-400" />
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold text-white">Venda de Garagem</h2>
                </div>
                <p className="text-zinc-400 ml-[52px]">Marketplace parceiro — compre e venda de tudo!</p>
              </div>
              <a
                href="https://garage-sell.preview.emergentagent.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="marketplace-view-all-btn"
                className="hidden md:flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition-all text-sm font-medium"
              >
                Ver todos os produtos
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="relative group">
              {/* Left arrow */}
              <button
                onClick={() => scrollCarousel('left')}
                data-testid="carousel-left-btn"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-zinc-800/90 border border-zinc-700 flex items-center justify-center text-white hover:bg-zinc-700 transition-all opacity-0 group-hover:opacity-100 -translate-x-1/2 shadow-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Carousel */}
              <div
                ref={carouselRef}
                data-testid="marketplace-carousel"
                className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-2"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {carouselProducts.map((product) => (
                  <a
                    key={product._id}
                    href={`https://garage-sell.preview.emergentagent.com/products/${product._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`product-card-${product._id}`}
                    className="flex-shrink-0 w-[280px] rounded-xl bg-zinc-800/60 border border-zinc-700/50 overflow-hidden hover:border-emerald-500/40 hover:shadow-lg hover:shadow-emerald-500/5 transition-all duration-300 group/card"
                  >
                    {/* Product Image */}
                    <div className="relative h-48 bg-zinc-900 overflow-hidden">
                      {getProductImage(product.images) ? (
                        <img
                          src={getProductImage(product.images)}
                          alt={product.title}
                          className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="w-12 h-12 text-zinc-700" />
                        </div>
                      )}
                      {/* Condition badge */}
                      <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-md text-xs font-medium ${
                        product.condition === 'new'
                          ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                          : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                      }`}>
                        {product.condition === 'new' ? 'Novo' : 'Usado'}
                      </span>
                    </div>

                    {/* Product Info */}
                    <div className="p-4">
                      <p className="text-white font-medium text-sm line-clamp-2 mb-2 min-h-[40px] group-hover/card:text-emerald-300 transition-colors">
                        {product.title}
                      </p>
                      <p className="text-emerald-400 font-bold text-lg mb-2">
                        {product.price?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                      <div className="flex items-center gap-2 text-zinc-500 text-xs">
                        <Tag className="w-3 h-3" />
                        <span>{product.category}</span>
                        {(product.city || product.location) && (
                          <>
                            <span className="text-zinc-700">|</span>
                            <MapPin className="w-3 h-3" />
                            <span>{product.city || product.location}{product.state ? `, ${product.state}` : ''}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </a>
                ))}
              </div>

              {/* Right arrow */}
              <button
                onClick={() => scrollCarousel('right')}
                data-testid="carousel-right-btn"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-zinc-800/90 border border-zinc-700 flex items-center justify-center text-white hover:bg-zinc-700 transition-all opacity-0 group-hover:opacity-100 translate-x-1/2 shadow-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile CTA */}
            <div className="mt-6 md:hidden text-center">
              <a
                href="https://garage-sell.preview.emergentagent.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition-all text-sm font-medium"
              >
                Ver todos os produtos
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Perguntas Frequentes</h2>
            <p className="text-zinc-400">Tire suas dúvidas sobre o WISE EAGLE</p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-zinc-800/50 rounded-xl border border-zinc-700 overflow-hidden"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full p-5 flex items-center justify-between text-left"
                >
                  <span className="font-semibold text-white pr-4">{faq.q}</span>
                  <ChevronRight
                    className={`w-5 h-5 text-zinc-400 transition-transform flex-shrink-0 ${
                      activeFaq === i ? 'rotate-90' : ''
                    }`}
                  />
                </button>
                {activeFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-400">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-zinc-900/50 to-zinc-950">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/30 mb-6">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-emerald-300">10 créditos grátis para novos usuários</span>
          </div>

          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Comece a se proteger
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent"> agora</span>
          </h2>
          <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
            Não espere ser vítima de um golpe. Cadastre-se gratuitamente e tenha acesso 
            à proteção mais avançada contra conteúdos falsos.
          </p>

          <button
            onClick={onGetStarted}
            className="px-10 py-4 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold text-lg transition-all shadow-xl shadow-cyan-500/30"
          >
            Criar Conta Grátis
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/wise_eagle_logo.png" alt="WISE EAGLE" className="w-10 h-10 rounded-full" />
                <span className="font-bold text-white">WISE EAGLE</span>
              </div>
              <p className="text-sm text-zinc-500">
                Protegendo você e sua família contra golpes digitais e conteúdos falsos.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Produto</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><a href="#features" className="hover:text-white transition-colors">Funcionalidades</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Preços</a></li>
                <li><a href="#referral" className="hover:text-white transition-colors">Programa de Indicação</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li><a href="#" className="hover:text-white transition-colors">Privacidade</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Termos de Uso</a></li>
                <li><a href="#" className="hover:text-white transition-colors">LGPD</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Contato</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  contato@wiseeagle.online
                </li>
                <li className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  wiseeagle.online
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-800 text-center text-sm text-zinc-500">
            <p>WISE EAGLE © 2026 - Todos os direitos reservados</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
